import { useState } from 'react'
import { ImageOff } from 'lucide-react'
import { cn } from '@/lib/utils'

type ImageWithFallbackProps = React.ComponentProps<'img'> & {
  /** Classes applied only to the fallback placeholder (in addition to className). */
  fallbackClassName?: string
}

/**
 * `<img>` that swaps to a neutral placeholder (ImageOff icon) when the source
 * fails to load — a deleted object (404) or a transient proxy error (502)
 * otherwise renders as the browser's broken-image glyph with no feedback.
 * The placeholder inherits `className` so it occupies the same box.
 */
export function ImageWithFallback({
  className,
  fallbackClassName,
  onError,
  src,
  alt,
  style,
  ...props
}: ImageWithFallbackProps) {
  // Track the src that failed (instead of a boolean) so a src change —
  // e.g. a cache-busted retry after re-upload — automatically resets.
  const [failedSrc, setFailedSrc] = useState<string | null>(null)

  if (!src || failedSrc === src) {
    return (
      <div
        role="img"
        aria-label={alt}
        style={style}
        className={cn(
          'flex items-center justify-center bg-slate-100 text-slate-400',
          className,
          fallbackClassName,
        )}
      >
        <ImageOff className="w-6 h-6 max-w-[40%] max-h-[40%]" />
      </div>
    )
  }

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      style={style}
      onError={(e) => {
        setFailedSrc(src)
        onError?.(e)
      }}
      {...props}
    />
  )
}
